import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type { Resolvable } from '@/services/resolvers/types'
import { isStringArray } from '@/utils/string/isStringArray'
import type RouterService from '@/services/route/routerService'
import type { RoutePrefetchService } from '@/services/route/routePrefetchService'
import type { LocalStorageService } from '@/services/storage/localStorageService'
import { COMPOSE_STORAGE_KEYS } from '@/services/domain/compose/const'
import { composeEmailRouteName, composeMessageRouteName } from '@/routes/user/compose'
import type { ComposeToggleValue } from '@/services/domain/compose/types'
import type MainSidebarRoutesResolverService from '@/services/resolvers/containers/mainSidebarRoutesResolverService'
import type { MainSidebarRouteInfo } from '@/services/resolvers/containers/mainSidebarRoutesResolverService'

@injectable()
export default class MainSidebarRoutesPrefetchResolverService implements Resolvable {
  constructor(
    @inject(SERVICE_TYPES.RouterService)
    protected readonly routerService: RouterService,
    @inject(SERVICE_TYPES.RoutePrefetchService)
    protected readonly routePrefetchService: RoutePrefetchService,
    @inject(SERVICE_TYPES.LocalStorageService)
    protected readonly localStorageService: LocalStorageService,
    @inject(SERVICE_TYPES.MainSidebarRoutesResolverService)
    protected readonly mainSidebarRoutesResolverService: MainSidebarRoutesResolverService,
  ) {}

  private prefetchRoutes(items: MainSidebarRouteInfo[]) {
    const routeNames: Set<string> = new Set()

    for (const route of items) {
      if (typeof route === 'string') {
        routeNames.add(route)
      } else if (isStringArray(route)) {
        for (const r of route) {
          routeNames.add(r)
        }
      } else {
        routeNames.add(route.routeName)
      }
    }

    for (const r of routeNames) {
      const resolved = this.routerService.resolve({ name: r })
      if (resolved.name) {
        this.routePrefetchService.prefetchChunksForRoute(resolved.name)
      }
    }
  }

  public async resolve() {
    const routes = Object.values(this.mainSidebarRoutesResolverService.getRoutesMapStructure())
    const composeToggleSavedValue = this.localStorageService.get(
      COMPOSE_STORAGE_KEYS.SELECTED_TYPE,
    ) as ComposeToggleValue | null

    if (composeToggleSavedValue === 'email') {
      routes.push(composeEmailRouteName)
    } else {
      routes.push(composeMessageRouteName)
    }

    this.prefetchRoutes(routes)
  }
}
